import styled, { css } from 'styled-components'
import { Surface } from '../../components/DashboardPrimitives'
import { WINDOW_SIZE } from '../../util/Constants'

// Analytics page styling. Banking-app minimalism on the shared dashboard
// system: one summary strip with hairline separators, one large chart, two
// quiet breakdown panels. 8px spacing grid, tabular numerals on every amount,
// theme tokens everywhere, transitions on hover states only.

export const focusRing = css`
  &:focus-visible {
    box-shadow: 0 0 0 3px rgba(49, 87, 198, 0.28);
    outline: none;
  }
`

export const AnalyticsWorkspace = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin: 4px auto 0;
  max-width: 1080px;
  min-width: 0;
  width: 100%;
`

export const HeaderRow = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  min-width: 0;
`

export const HeaderPillSlot = styled.div`
  display: flex;
  flex: none;
  padding-top: 3px;
`

// ---------------------------------------------------------------------------
// Summary strip: one Surface row, stats separated by hairlines
// ---------------------------------------------------------------------------

export const StatStrip = styled(Surface)`
  flex-direction: row;
  gap: 0;
  padding: 15px 6px;

  @media (max-width: ${WINDOW_SIZE.TABLET}) {
    display: grid;
    gap: 16px 0;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`

export const StatCell = styled.div`
  border-left: 1px solid var(--hairline, #eef1f5);
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
  padding: 2px 18px;

  &:first-child {
    border-left: 0;
    padding-left: 12px;
  }

  @media (max-width: ${WINDOW_SIZE.TABLET}) {
    border-left: 0;
    padding: 2px 12px;

    &:first-child {
      padding-left: 12px;
    }

    &:nth-child(even) {
      border-left: 1px solid var(--hairline, #eef1f5);
    }
  }
`

export const StatLabel = styled.span`
  color: var(--text-muted, #6f7888);
  font-size: 11.5px;
  font-weight: 500;
  letter-spacing: 0.05em;
  line-height: 1.25;
  text-transform: uppercase;
  white-space: nowrap;
`

export const StatValueRow = styled.div`
  align-items: baseline;
  column-gap: 8px;
  display: flex;
  flex-wrap: wrap;
  min-height: 25px;
  row-gap: 2px;
`

export const StatValue = styled.span`
  color: var(--ink-strong, #121722);
  font-size: 21px;
  font-variant-numeric: tabular-nums;
  font-weight: 550;
  letter-spacing: -0.01em;
  line-height: 1.15;
  overflow-wrap: anywhere;
`

export const StatDelta = styled.span<{ $tone: 'up' | 'down' | 'flat' }>`
  color: ${({ $tone }) => $tone === 'up'
    ? 'var(--pill-green-ink, #16724b)'
    : $tone === 'down'
      ? 'var(--pill-red-ink, #c51735)'
      : 'var(--text-faint, #8a919e)'};
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
`

// ---------------------------------------------------------------------------
// Chart panel
// ---------------------------------------------------------------------------

export const ChartSurface = styled(Surface)`
  gap: 12px;
  padding: 17px 18px 13px;
`

export const ChartHead = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  min-width: 0;

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    flex-direction: column;
  }
`

export const PanelLabelStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`

export const PanelLabel = styled.span`
  color: var(--text-muted, #6f7888);
  font-size: 11.5px;
  font-weight: 500;
  letter-spacing: 0.05em;
  line-height: 1.25;
  text-transform: uppercase;
`

export const PanelMeta = styled.span`
  color: var(--text-soft, #7d8492);
  font-size: 12px;
  line-height: 1.4;
`

export const Segmented = styled.div`
  background: var(--surface-hover, #f4f6fa);
  border-radius: 999px;
  display: inline-flex;
  flex: none;
  gap: 2px;
  padding: 2px;
  width: fit-content;
`

export const SegmentedButton = styled.button<{ $active?: boolean }>`
  background: ${({ $active }) => $active ? 'var(--surface, #ffffff)' : 'transparent'};
  border: 0;
  border-radius: 999px;
  box-shadow: ${({ $active }) => $active ? '0 1px 2px rgba(20, 28, 45, 0.08)' : 'none'};
  color: ${({ $active }) => $active ? 'var(--ink, #20242d)' : 'var(--text-muted, #697386)'};
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
  line-height: 1.2;
  min-height: 26px;
  padding: 0 11px;
  transition: background-color 0.16s ease, color 0.16s ease;

  &:hover {
    color: var(--ink, #20242d);
  }

  ${focusRing}
`

// ---------------------------------------------------------------------------
// Breakdown panels
// ---------------------------------------------------------------------------

export const BreakdownGrid = styled.div`
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

export const BreakdownSurface = styled(Surface)`
  gap: 2px;
  padding: 16px 18px 14px;
`

export const BreakdownList = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 8px;
`

export const BreakdownRow = styled.div`
  border-top: 1px solid var(--hairline-soft, #f1f4f9);
  display: flex;
  flex-direction: column;
  gap: 7px;
  padding: 10px 0 12px;

  &:first-child {
    border-top: 0;
    padding-top: 2px;
  }

  &:last-child {
    padding-bottom: 4px;
  }
`

export const BreakdownTopRow = styled.div`
  align-items: baseline;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  min-width: 0;
`

export const BreakdownName = styled.span`
  align-items: center;
  color: var(--ink, #20242d);
  display: inline-flex;
  font-size: 13px;
  font-weight: 500;
  gap: 8px;
  line-height: 1.3;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const BreakdownFigures = styled.span`
  align-items: baseline;
  display: inline-flex;
  flex: none;
  gap: 8px;
`

export const BreakdownAmount = styled.span`
  color: var(--ink-strong, #121722);
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  font-weight: 550;
  white-space: nowrap;
`

export const BreakdownShare = styled.span`
  color: var(--text-faint, #8a919e);
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  min-width: 34px;
  text-align: right;
  white-space: nowrap;
`

export const BarTrack = styled.div`
  background: var(--hairline, #eef1f5);
  border-radius: 999px;
  height: 3px;
  overflow: hidden;
  width: 100%;
`

export const BarFill = styled.div<{ $color?: string }>`
  background: ${({ $color }) => $color ?? 'var(--ink-strong, #121722)'};
  border-radius: 999px;
  height: 100%;
  opacity: ${({ $color }) => $color ? 0.9 : 0.55};
`

export const AssetLogo = styled.img`
  border-radius: 50%;
  flex: none;
  height: 16px;
  width: 16px;
`

export const LoadingRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 10px 0 6px;
`

export const MutedNote = styled.span`
  color: var(--text-soft, #7d8492);
  font-size: 12.5px;
  line-height: 1.45;
  padding: 8px 0 4px;
`

export const StatSpark = styled.div`
  margin-top: 8px;
  opacity: 0.9;
  width: 100%;
`

export const CompositionTrack = styled.div`
  border-radius: 99px;
  display: flex;
  height: 6px;
  overflow: hidden;
  width: 100%;
`

export const CompositionSegment = styled.div<{ $color: string, $width: number }>`
  background: ${({ $color }) => $color};
  min-width: ${({ $width }) => $width > 0 ? '3px' : '0'};
  transition: width 0.3s ease;
  width: ${({ $width }) => $width}%;
`

export const LegendDot = styled.span<{ $color: string }>`
  background: ${({ $color }) => $color};
  border-radius: 99px;
  display: inline-block;
  flex: 0 0 auto;
  height: 8px;
  width: 8px;
`

export const PanelStatRow = styled.div`
  align-items: baseline;
  display: flex;
  justify-content: space-between;
  padding: 9px 0;

  & + & {
    border-top: 1px solid var(--hairline-soft, #f0f2f6);
  }
`

export const PanelStatLabel = styled.span`
  color: var(--text-muted, #566071);
  font-size: 13px;
`

export const PanelStatValue = styled.span<{ $tone?: 'green' | 'red' }>`
  color: ${({ $tone }) => $tone === 'green'
    ? 'var(--pill-green-ink, #16724b)'
    : $tone === 'red'
      ? 'var(--pill-red-ink, #c51735)'
      : 'var(--ink, #20242d)'};
  font-size: 16px;
  font-variant-numeric: tabular-nums;
  font-weight: 640;
`
